import { ChangeDetectionStrategy, Component, OnInit, computed, signal } from '@angular/core';
import { DashboardApi } from 'api/dashboard/dashboard-api.js';
import { AppointmentDb } from 'lib/cosmos/appointment/appointmentDb.js';
import { CosmosDb } from 'lib/cosmos/cosmosDb.js';
import { PackageDb } from 'lib/cosmos/package/packageDb.js';
import { TaskDb } from 'lib/cosmos/task/taskDb.js';
import { UserDb } from 'lib/cosmos/user/userDb.js';
import { isNotUndefined } from 'lib/helpers/array.utils';
import { unitConversionHelper } from 'lib/helpers/unit-conversion-helper.class.js';
import { IButton } from 'lib/ui/buttons/index.js';
import { ListItem } from 'lib/ui/list/index.js';
import { SimpleTableItem } from 'lib/ui/simple-table/index.js';
import { IImage } from 'lib/ui/ui-models.js';
import { Observable, map } from 'rxjs';
import { TypeCoreComponent } from 'src/app/pages/type-routes/type-core.component.js';

type CommonDashboardData = {
	readonly training?: {
		readonly planName: string;
		readonly routerLink: string;
	};
	readonly nutrition?: {
		readonly planName: string;
		readonly routerLink: string;
	};
};

type BrandInfo = {
	readonly name: string;
	readonly bio?: string;
	readonly tel?: string;
	readonly brandAvatar: IImage | undefined;
	readonly tableItems: readonly SimpleTableItem[];
};

type ColumnLocation = 'left' | 'right';

@Component({
	selector: 'mod-client-dashboard',
	templateUrl: './client-dashboard.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class ClientDashboardComponent extends TypeCoreComponent<UserDb.IUserItem> implements OnInit {
	private readonly clientDashboardData = signal<DashboardApi.ClientDashboardData | undefined>(undefined);
	private readonly linkedItems = signal<readonly CosmosDb.ICosmosItem[]>([]);

	protected readonly brandInfoBoxLocation = computed<ColumnLocation>(() => {
		if (this.isMobileView()) {
			return 'left';
		}

		return 'right';
	});

	protected readonly trainerButtons = computed<readonly IButton[]>(() => {
		const sharedData = this.sharedData();

		if (!sharedData) {
			return [];
		}

		return [
			{
				title: 'Send message',
				icon: 'comment',
				routerLink: super
					.actionRoute((routes) => routes.chatRoutes.conversation.children.view, sharedData.data.trainerId)
					.getUrl()
			}
		];
	});

	protected readonly nutritionPlanButtons = computed<readonly IButton[]>(() => {
		const commonDashboardData = this.commonDashboardData();

		if (!commonDashboardData || !commonDashboardData.nutrition) {
			return [];
		}

		return [
			{
				title: 'View nutrition plan',
				buttonType: 'primaryLight',
				routerLink: commonDashboardData.nutrition.routerLink
			}
		];
	});

	protected readonly trainingPlanButtons = computed<readonly IButton[]>(() => {
		const commonDashboardData = this.commonDashboardData();

		if (!commonDashboardData || !commonDashboardData.training) {
			return [];
		}

		return [
			{
				title: 'View training plan',
				buttonType: 'primaryLight',
				routerLink: commonDashboardData.training.routerLink
			}
		];
	});

	protected readonly brandInfo = computed<BrandInfo>(() => {
		const dashboardData = this.clientDashboardData();
		const asignedPackages = this.assignedPackages();
		const trainer = dashboardData?.trainer;
		const bio = trainer?.branding?.brandBio;
		const tel = trainer?.branding?.brandTel;

		const simpleTableItems: readonly (SimpleTableItem | undefined)[] = [
			trainer
				? {
						title: 'E-mail',
						value: trainer.email,
						valueType: 'text'
				  }
				: undefined,
			tel
				? {
						title: 'Tel',
						value: tel,
						valueType: 'text'
				  }
				: undefined,
			trainer?.location
				? {
						title: 'Location',
						value: trainer.location,
						valueType: 'text'
				  }
				: undefined,
			trainer?.timezone
				? {
						title: 'Timezone',
						value: trainer.timezone,
						valueType: 'text'
				  }
				: undefined,
			...(asignedPackages?.length
				? asignedPackages.map<SimpleTableItem>((packageItem) => {
						return {
							title: 'Assigned package',
							value: `${packageItem.system.name}${packageItem.price ? ` ${packageItem.price}` : ''}`,
							valueType: 'tag',
							design: 'tag'
						};
				  })
				: [])
		];

		return {
			name: trainer?.fullName ?? '',
			bio: bio,
			brandAvatar: trainer?.avatar,
			tel: tel,
			tableItems: simpleTableItems.filter(isNotUndefined)
		};
	});

	protected readonly commonDashboardData = computed<CommonDashboardData | undefined>(() => {
		const sharedData = this.sharedData();

		if (!sharedData) {
			return undefined;
		}

		return {
			training: sharedData.data.training
				? {
						planName: sharedData.data.training.title,
						routerLink: super
							.actionRoute((routes) => routes.clientRoutes.training.children.overview)
							.getUrl()
				  }
				: undefined,
			nutrition: sharedData.data.nutrition
				? {
						planName: sharedData.data.nutrition.title,
						routerLink: super
							.actionRoute((routes) => routes.clientRoutes.nutrition.children.overview)
							.getUrl()
				  }
				: undefined
		};
	});

	protected readonly upcomingAppointments = computed<readonly ListItem<AppointmentDb.IAppointmentItem>[] | undefined>(
		() => {
			const dashboardData = this.clientDashboardData();

			if (!dashboardData) {
				return undefined;
			}

			return dashboardData.upcomingAppointments.map<ListItem<AppointmentDb.IAppointmentItem>>((appointment) => {
				return {
					data: appointment,
					id: appointment.id,
					title: appointment.system.name,
					avatar: dashboardData.trainer.avatar,
					subtitle: `${appointment.length}min. | ${super.formatDateTime(appointment.dateTime)}`,
					routerLink: super.actionRoute((routes) => routes.calendarRoutes.view.children.upcoming).getUrl()
				};
			});
		}
	);

	protected readonly assignedFiles = computed<readonly ListItem<CosmosDb.ICosmosFileReference>[] | undefined>(() => {
		const sharedData = this.sharedData();

		if (!sharedData) {
			return undefined;
		}

		return (sharedData.data.clientFiles ?? []).map<ListItem<CosmosDb.ICosmosFileReference>>((file) => {
			return {
				data: file,
				id: file.absoluteUrl,
				title: file.originalFilename,
				avatar: file.image,
				subtitle: `${unitConversionHelper.formatBytes(file.sizeInBytes, 0)}`,
				externalUrlLink: file.absoluteUrl
			};
		});
	});

	protected readonly assignedTasks = computed<readonly ListItem<TaskDb.ITaskItem>[] | undefined>(() => {
		const sharedData = this.sharedData();

		if (!sharedData) {
			return undefined;
		}

		return (sharedData.data.assignedTasks ?? [])
			.map<ListItem<TaskDb.ITaskItem> | undefined>((assignedTask) => {
				const task = super.getLinkedItemById<TaskDb.ITaskItem>(assignedTask.taskId);

				if (!task) {
					return undefined;
				}

				const item: ListItem<TaskDb.ITaskItem> = {
					data: task,
					id: task.id,
					title: task.system.name,
					subtitle: task.description,
					avatar: task.avatar?.[0]?.image,
					routerLink: super.actionRoute((routes) => routes.clientRoutes.tasks.children.overview).getUrl()
				};

				return item;
			})
			.filter(isNotUndefined);
	});

	protected readonly assignedPackages = computed<readonly PackageDb.IPackageItem[] | undefined>(() => {
		const sharedData = this.sharedData();

		if (!sharedData) {
			return undefined;
		}

		return (sharedData.data.assignedPackages ?? [])
			.map((assignedPackage) => {
				const packageItem = super.getLinkedItemById<PackageDb.IPackageItem>(assignedPackage.packageId);

				if (!packageItem) {
					return undefined;
				}

				return packageItem;
			})
			.filter(isNotUndefined);
	});

	protected readonly showBrandInfo = computed<boolean>(() => {
		if (this.assignedPackages()?.length || this.brandInfo()) {
			return true;
		}
		return false;
	});

	constructor() {
		super();

		this.dataInit = () => {
			super.subscribeToObservable(this.initClientDashboardData());
		};
	}

	private initClientDashboardData(): Observable<void> {
		return this.dependencies.httpService
			.get<DashboardApi.ClientDashboardData>((builder) =>
				builder.withCustomApiRoute('dashboard', 'getClientDashboardData').build()
			)
			.pipe(
				map((response) => {
					this.clientDashboardData.set(response.data);
					this.linkedItems.set(response.linkedItems ?? []);
				})
			);
	}
}
